import React from 'react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { 
  Calendar, Clock, User, ArrowLeft, Share2, 
  Tag, MessageSquare, Heart, Bookmark
} from 'lucide-react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';

const NewsDetailsPage = () => {
  const { id } = useParams();

  // Mock article data - in a real app, this would come from an API
  const article = {
    id: id,
    title: "AxonMetric Launches Advanced AI-Powered Analytics Platform",
    excerpt: "New platform combines machine learning with real-time monitoring to revolutionize renewable energy asset management",
    content: `
      <p>AxonMetric Renewables has announced the launch of its next-generation AI-powered analytics platform, designed to transform how renewable energy operators monitor and optimize their assets. The new platform leverages advanced machine learning algorithms to provide predictive insights and automated optimization recommendations.</p>
      
      <h2>Revolutionary Features</h2>
      <p>The platform introduces several groundbreaking features that set it apart from traditional monitoring solutions:</p>
      <ul>
        <li><strong>Predictive Analytics:</strong> Advanced AI models predict potential equipment failures before they occur</li>
        <li><strong>Real-time Optimization:</strong> Continuous monitoring and automatic adjustment of operational parameters</li>
        <li><strong>Portfolio Management:</strong> Unified dashboard for managing multiple renewable energy sites</li>
        <li><strong>Automated Reporting:</strong> Intelligent report generation with actionable insights</li>
      </ul>
      
      <h2>Industry Impact</h2>
      <p>This launch represents a significant milestone in the renewable energy industry. The platform's capabilities have already been demonstrated in pilot programs, showing:</p>
      <ul>
        <li>40% reduction in equipment downtime</li>
        <li>25% improvement in energy production efficiency</li>
        <li>60% faster issue resolution times</li>
        <li>15% reduction in operational costs</li>
      </ul>
      
      <h2>Technical Innovation</h2>
      <p>The platform utilizes cutting-edge technologies including:</p>
      <ul>
        <li>Machine learning algorithms trained on millions of data points</li>
        <li>IoT sensor integration for comprehensive monitoring</li>
        <li>Cloud-native architecture for scalability and reliability</li>
        <li>Advanced data visualization and reporting tools</li>
      </ul>
      
      <h2>Future Roadmap</h2>
      <p>AxonMetric plans to continue innovating with upcoming features including:</p>
      <ul>
        <li>Integration with blockchain for transparent energy trading</li>
        <li>Advanced weather prediction and optimization</li>
        <li>Mobile applications for field technicians</li>
        <li>API ecosystem for third-party integrations</li>
      </ul>
    `,
    category: "technology",
    author: "Sarah Johnson",
    date: "2024-01-15",
    readTime: "5 min read",
    image: "/assets/img/news/ai-platform.jpg",
    gradient: "from-blue-500 to-cyan-500",
    tags: ["AI", "Analytics", "Platform", "Innovation"],
    relatedArticles: [
      {
        id: 2,
        title: "Record-Breaking Performance at Solar Farm Operations",
        excerpt: "AxonMetric's monitoring solutions help achieve 98.5% efficiency at major solar installations",
        date: "2024-01-10",
        gradient: "from-green-500 to-emerald-500"
      },
      {
        id: 4,
        title: "New Predictive Maintenance Features Reduce Downtime by 40%",
        excerpt: "Latest platform update introduces advanced predictive maintenance capabilities",
        date: "2023-12-28",
        gradient: "from-orange-500 to-red-500"
      }
    ]
  };

  const safeArticleContent = DOMPurify.sanitize(article.content);

  return (
    <Layout header={6} footer={6}>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-400/10 to-cyan-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-400/10 to-pink-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <Link
                to="/news"
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors duration-300"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to News</span>
              </Link>
            </motion.div>

            {/* Article Header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center mb-12"
            >
              <div className="inline-block bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-6">
                {article.category.charAt(0).toUpperCase() + article.category.slice(1)}
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-gray-800 via-blue-800 to-gray-800 bg-clip-text text-transparent mb-6 leading-tight">
                {article.title}
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
                {article.excerpt}
              </p>

              {/* Article Meta */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 mb-8">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>{article.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(article.date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{article.readTime}</span>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap justify-center gap-2 mb-8">
                {article.tags.map((tag, index) => (
                  <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Social Share */}
              <div className="flex justify-center gap-4">
                <button className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300">
                  <Share2 className="w-5 h-5" />
                </button>
                <button className="w-10 h-10 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300">
                  <Heart className="w-5 h-5" />
                </button>
                <button className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform duration-300">
                  <Bookmark className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20 bg-white">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="prose prose-lg max-w-none"
            >
              {/* Featured Image */}
              <div className={`w-full h-64 bg-gradient-to-br ${article.gradient} rounded-3xl mb-12 flex items-center justify-center relative overflow-hidden`}>
                <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent"></div>
                <div className="text-center text-white relative z-10">
                  <h3 className="text-2xl font-bold mb-4">Featured Image</h3>
                  <p className="text-lg text-white/90">AI-Powered Analytics Platform</p>
                </div>
              </div>

              {/* Article Content */}
              <div 
                className="text-gray-700 leading-relaxed text-lg"
                dangerouslySetInnerHTML={{ __html: safeArticleContent }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-800 via-blue-800 to-gray-800 bg-clip-text text-transparent mb-4">
                Related Articles
              </h2>
              <p className="text-xl text-gray-600">
                Explore more insights and updates from AxonMetric
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {article.relatedArticles.map((related, index) => (
                <motion.article
                  key={related.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="group relative"
                >
                  <Link to={`/news/${related.id}`}>
                    <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl overflow-hidden hover:bg-white/90 transition-all duration-500 hover:scale-105 shadow-xl hover:shadow-2xl relative">
                      {/* Article Image Placeholder */}
                      <div className={`h-48 bg-gradient-to-br ${related.gradient} relative overflow-hidden`}>
                        <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent"></div>
                      </div>
                      
                      {/* Article Content */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-gray-900 transition-colors duration-300">
                          {related.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                          {related.excerpt}
                        </p>
                        
                        {/* Article Meta */}
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(related.date).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      {/* Hover Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%]"></div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl lg:text-5xl font-bold text-white mb-6"
            >
              Ready to Experience Our Platform?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-gray-300 mb-8 leading-relaxed"
            >
              Discover how our AI-powered analytics can transform your renewable energy operations
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-4 px-8 rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 text-lg"
              >
                <span>Schedule a Demo</span>
                <ArrowLeft className="w-5 h-5 group-hover:translate-x-1 transition-transform rotate-180" />
              </Link>
              <Link
                to="/service"
                className="group inline-flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-bold py-4 px-8 rounded-2xl hover:bg-white/20 transition-all duration-300 transform hover:scale-105 text-lg"
              >
                <span>Learn More</span>
                <ArrowLeft className="w-5 h-5 group-hover:translate-x-1 transition-transform rotate-180" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default NewsDetailsPage;
